
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BarChart, BarChart2, PieChart } from "lucide-react";
import { BarChart as RechartsBarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, PieChart as RechartsPieChart, Pie, Cell } from "recharts";
import { RFPEstimate } from "@/types/project-types";
import { getComponentTooltip } from "@/data/estimationData";
import { ComponentType } from "@/types/estimation-types";

interface AnalyticsTabProps {
  componentEstimates: RFPEstimate[];
}

const AnalyticsTab: React.FC<AnalyticsTabProps> = ({ componentEstimates }) => {
  // Filter out any estimates with no count values to avoid empty data
  const validEstimates = componentEstimates.filter(est => 
    est.simple_count > 0 || 
    est.medium_count > 0 || 
    est.complex_count > 0 || 
    est.supercomplex_count > 0
  );
  
  // Prepare data for the component type bar chart
  const componentTypeData = validEstimates.map(est => ({
    name: est.component_type,
    points: est.total_points,
    days: Math.round(est.total_days * 10) / 10,
    dev_days: Math.round(est.total_dev_days * 10) / 10,
    tooltipText: getComponentTooltip(est.component_type as ComponentType)
  }));

  // Prepare data for the complexity distribution pie chart
  const complexityData = [
    { name: 'Simple', value: validEstimates.reduce((acc, est) => acc + est.simple_count, 0) },
    { name: 'Medium', value: validEstimates.reduce((acc, est) => acc + est.medium_count, 0) },
    { name: 'Complex', value: validEstimates.reduce((acc, est) => acc + est.complex_count, 0) },
    { name: 'Supercomplex', value: validEstimates.reduce((acc, est) => acc + est.supercomplex_count, 0) }
  ].filter(item => item.value > 0);

  const totalPoints = validEstimates.reduce((acc, est) => acc + est.total_points, 0);
  const totalDays = validEstimates.reduce((acc, est) => acc + est.total_days, 0);
  const totalDevDays = validEstimates.reduce((acc, est) => acc + est.total_dev_days, 0);

  // Colors for the pie chart
  const COLORS = ['#4cc9f0', '#4895ef', '#4361ee', '#3f37c9'];

  // If there's no valid data, show a message
  if (validEstimates.length === 0) {
    return (
      <div className="space-y-4 pt-4">
        <Card className="bg-white dark:bg-gray-900 border-purple-100 dark:border-gray-800">
          <CardHeader>
            <CardTitle className="text-xl text-purple-700 dark:text-gray-300">Estimation Analytics</CardTitle>
          </CardHeader>
          <CardContent className="py-6">
            <div className="flex flex-col items-center justify-center min-h-[300px] text-center">
              <BarChart2 className="w-16 h-16 text-purple-300 mb-4" />
              <h3 className="text-lg font-medium">No Data Available</h3>
              <p className="text-gray-500 max-w-md mt-2">
                Add component estimates in the Estimation tab to view analytics.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 pt-4">
      {/* Summary Card */}
      <Card className="bg-white dark:bg-gray-900 border-purple-100 dark:border-gray-800">
        <CardHeader>
          <CardTitle className="text-xl text-purple-700 dark:text-gray-300">Estimation Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
              <CardContent className="pt-6 pb-4 text-center">
                <h3 className="text-sm font-medium mb-2 opacity-90">Total Story Points</h3>
                <p className="text-3xl font-bold">{totalPoints}</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
              <CardContent className="pt-6 pb-4 text-center">
                <h3 className="text-sm font-medium mb-2 opacity-90">Total Dev Days</h3>
                <p className="text-3xl font-bold">{Math.round(totalDevDays * 10) / 10}</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
              <CardContent className="pt-6 pb-4 text-center">
                <h3 className="text-sm font-medium mb-2 opacity-90">Total Work Days</h3>
                <p className="text-3xl font-bold">{Math.round(totalDays * 10) / 10}</p>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>

      {/* Component Type Bar Chart */}
      <Card className="bg-white dark:bg-gray-900 border-purple-100 dark:border-gray-800">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-xl text-purple-700 dark:text-gray-300">Component Types</CardTitle>
          <BarChart className="h-5 w-5 text-gray-500" />
        </CardHeader>
        <CardContent>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <RechartsBarChart
                data={componentTypeData}
                margin={{
                  top: 20, right: 30, left: 20, bottom: 70,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" angle={-45} textAnchor="end" interval={0} height={70} />
                <YAxis />
                <Tooltip 
                  formatter={(value, name) => [value, name === 'days' ? 'Total Days' : name === 'dev_days' ? 'Dev Days' : 'Story Points']}
                  labelFormatter={(label) => {
                    const item = componentTypeData.find(item => item.name === label);
                    return `${label}: ${item?.tooltipText || ''}`;
                  }}
                />
                <Legend />
                <Bar dataKey="points" name="Story Points" fill="#4361ee" />
                <Bar dataKey="dev_days" name="Dev Days" fill="#4cc9f0" />
                <Bar dataKey="days" name="Total Days" fill="#3f37c9" />
              </RechartsBarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Complexity Distribution Pie Chart */}
      <Card className="bg-white dark:bg-gray-900 border-purple-100 dark:border-gray-800">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-xl text-purple-700 dark:text-gray-300">Complexity Distribution</CardTitle>
          <PieChart className="h-5 w-5 text-gray-500" />
        </CardHeader>
        <CardContent>
          <div className="h-80 flex items-center justify-center">
            <ResponsiveContainer width="100%" height="100%">
              <RechartsPieChart>
                <Pie
                  data={complexityData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={120}
                  fill="#8884d8"
                  paddingAngle={5}
                  dataKey="value"
                  label={({name, percent}) => `${name}: ${(percent * 100).toFixed(0)}%`}
                >
                  {complexityData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => [`${value} components`, 'Count']} />
                <Legend />
              </RechartsPieChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AnalyticsTab;
